import { Inline, Block } from '@contentful/rich-text-types';
import { Next } from '@contentful/rich-text-html-renderer';
import { useCheckIfValueExistsInDisplayOptions } from '@/composables/useCheckIfValueExistsInDisplayOptions';

export const getProperParagraphOrSpace = (node, next) => {
  if (node.content && node.content.some((content) => content.value != '')) {
    return `<p>${next(node.content)}</p>`;
  } else {
    return '<br/>';
  }
};

export const embeddedAssetBlockAsImg = (node) => {
  const getDataFromNode = (node, type) => {
    switch (type) {
      case 'src':
        return node.data?.target?.fields?.file?.url || '';
      case 'alt':
        return node.data?.target?.fields?.description || '';
    }
  };
  return `<img style="width: 100%; max-width: 400px;" src="${getDataFromNode(node, 'src')}" alt="${getDataFromNode(
    node,
    'alt',
  )}"/>`;
};

export const getProperListItem = (displayOptions, node, next) => {
  let bulletListClass = '';
  if (useCheckIfValueExistsInDisplayOptions('max-5-items-leaf-bullet-list', displayOptions)) {
    bulletListClass = 'with-leaf';
  }
  return `<li class="${bulletListClass}">${next(node.content)}</li>`;
};

export const getProperList = (displayOptions, node, next) => {
  let bulletListClass = '';
  if (useCheckIfValueExistsInDisplayOptions('max-5-items-leaf-bullet-list', displayOptions)) {
    bulletListClass = 'list-with-leaf';
  }
  return `<ul class="${bulletListClass}">${next(node.content)}</ul>`;
};

export const getInternalLink = (
  node: Inline | Block,
  next: Next,
  links: Map<string, any> | null,
  injectedLocale: string | null,
): string => {
  if (links) {
    return getInternalLinkFromGraphQL(node, next, links, injectedLocale);
  }

  const target = node.data.target;

  const contentType = target?.sys?.contentType?.sys?.id;
  const locale = target?.sys?.locale;

  if (!node.content || !contentType || !locale) return '';

  const title = next(node.content);
  const slug = target.fields.slug;

  const localePrefix = locale === 'fr' ? '' : `/${locale}`;
  const pagePrefix = getPagePrefix(contentType?.toLowerCase(), locale);

  // If internal link isn't leading to a page, we are ignoring it
  if (!pagePrefix || !title) return '';

  // If for some reason we don't have a slug, we display only title as a fallback
  if (!slug) {
    return `<span>${title}</span>`;
  }

  const URL = `${localePrefix}/${pagePrefix}/${slug}`;
  return `<a href="${URL}">${title}</a>`;
};

const getInternalLinkFromGraphQL = (
  node: Inline | Block,
  next: Next,
  links: Map<string, any>,
  locale: string,
): string => {
  const title = next(node.content);
  const entryLink = links.get(node.data.target.sys.id);

  if (!entryLink || !entryLink.slug) {
    return `<span>${title}</span>`;
  }

  const localePrefix = locale === 'fr' ? '' : `/${locale}`;
  const pagePrefix = getPagePrefix(entryLink.__typename?.toLowerCase(), locale);

  const URL = `${localePrefix}/${pagePrefix}/${entryLink.slug}`;
  return `<a href="${URL}">${title}</a>`;
};

const getPagePrefix = (contentType: string, locale: string) => {
  switch (contentType) {
    case 'pagerecipelist':
      return 'recipes';
    case 'pagegenericcategory':
      return 'category';
    case 'pagerecipe':
      return 'recipe';
    case 'pageadvice':
      return 'page';
    case 'pagebasic':
      return 'content';
    case 'pageproduct':
      return locale === 'fr' ? 'info/fiche-technique' : 'product';
    case 'pageproductinformations':
      return locale === 'fr' ? 'info/fiche-technique' : 'guide';

    default:
      return false;
  }
};

/**
 * Used with richText links from Contentful GraphQL that separates the links from content
 * (not the same logic as the rest API)
 * Check this blog article for inspiration https://www.contentful.com/blog/rendering-linked-assets-entries-in-contentful/
 * Example of response data from advice page rich text description component
 * Map([
 *     [
 *         "7755ABiAgj20rGQT0pJSqa",
 *         {
 *             "__typename": "PageAdvice",
 *             "sys": {
 *                 "id": "7755ABiAgj20rGQT0pJSqa"
 *             },
 *             "slug": "rhume-nez-bouche-nez-qui-coule"
 *         }
 *     ], [...]
 * ])
 * @param links
 */
export const createRichTextLinksMap = (links: any): Map<string, any> | null => {
  if (!links) {
    return null;
  }

  const entryMap = new Map();

  /** create entry hyperlinks maps **/
  if (links.entries && links.entries.hasOwnProperty('hyperlink')) {
    const entriesHyperlinkItems = links.entries.hyperlink;

    for (const linkItem of entriesHyperlinkItems) {
      if (linkItem?.sys?.id) entryMap.set(linkItem.sys.id, linkItem);
    }
  }

  /** Add up here if others type of links are needed **/

  return entryMap;
};
